/**
 * Service Factory
 * Creates service instances for different API endpoints
 */
import apiClient from './apiClient';

/**
 * Create a service with standard CRUD operations
 * @param {string} baseUrl - Base URL for the service
 * @returns {Object} - Service with standard methods
 */
export const createService = (baseUrl) => {
  return {
    /**
     * Access to the API client for custom calls
     */
    client: apiClient,
    
    /**
     * Get all items
     * @param {Object} params - Query parameters
     * @returns {Promise} - Response with items
     */
    getAll(params = {}) {
      // Convert UI page (1-based) to API page (0-based)
      const apiParams = { ...params };
      if (typeof apiParams.page === 'number') {
        apiParams.page = Math.max(0, apiParams.page - 1);
      }
      
      return apiClient.get(baseUrl, { params: apiParams }).then(response => response.data);
    },
    
    /**
     * Get an item by ID
     * @param {string} id - Item ID
     * @returns {Promise} - Response with item details
     */
    getById(id) {
      return apiClient.get(`${baseUrl}/${id}`).then(response => response.data);
    },
    
    /**
     * Create a new item
     * @param {Object} data - Item data
     * @returns {Promise} - Response with created item
     */
    create(data) {
      return apiClient.post(baseUrl, data).then(response => response.data);
    },
    
    /**
     * Update an item
     * @param {string} id - Item ID
     * @param {Object} data - Item data
     * @returns {Promise} - Response with updated item
     */
    update(id, data) {
      return apiClient.put(`${baseUrl}/${id}`, data).then(response => response.data);
    },
    
    /**
     * Delete an item
     * @param {string} id - Item ID
     * @returns {Promise} - Response
     */
    delete(id) {
      return apiClient.delete(`${baseUrl}/${id}`).then(response => response.data);
    },
    
    /**
     * Search items with filters
     * @param {Object} searchParams - Search parameters
     * @returns {Promise} - Response with filtered items
     */
    search(searchParams = {}) {
      // Convert UI page (1-based) to API page (0-based)
      const apiParams = { ...searchParams };
      if (typeof apiParams.page === 'number') {
        apiParams.page = Math.max(0, apiParams.page - 1);
      }
      
      return apiClient.post(`${baseUrl}/search`, apiParams).then(response => response.data);
    }
  };
};

export default {
  createService
}; 